import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useImperativeHandle,
    useMemo
} from 'react';
import { Contact } from '@/api';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import { useUpdateCompanyUserAction } from '../actions';
import { useCompaniesTranslation } from '../hooks';
import { FullAccessCompanyUser } from '../util/company-user-access';
import {
    ContactData,
    EditableContactsList,
    ContactsList
} from '@/common/components';
import { sanitisePhone, uuidv4 } from '@/common/util';

type NextOfKinSettingsSectionProps = {
    companyUser: FullAccessCompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

export function MemberNextOfKinSettingsSection(
    props: NextOfKinSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;

    const { t } = useCompaniesTranslation();

    const formRef = useRef<MemberNextOfKinSettingsSectionFormRef>(null);
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_next_of_kin_settings_section.toast_updated'
        )
    });

    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: { contacts: Contact[] }) {
        const { contacts } = data;
        updateCompanyUser
            .updateCompanyUser(companyUser.id, {
                nextOfKin: contacts
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <MemberNextOfKinSettingsSectionForm
            canEdit={canEdit}
            editDisabledReason={editDisabledReason}
            ref={formRef}
            initialContacts={companyUser.contacts}
            loading={updateCompanyUser.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
        />
    );
}

type NextOfKinSettingsSectionFormProps = {
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
    initialContacts: Contact[];
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: { contacts: Contact[] }) => void;
};

type MemberNextOfKinSettingsSectionFormRef = {
    resetForm: () => void;
};

const MemberNextOfKinSettingsSectionForm = forwardRef<
    MemberNextOfKinSettingsSectionFormRef,
    NextOfKinSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        editDisabledReason,
        initialContacts,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);
    const [contacts, setContacts] = useState<ContactData[]>(
        initialContacts.map((contact) => ({
            id: uuidv4(),
            isValid: false,
            ...contact
        }))
    );

    /**
     * Contacts
     */
    const checkContacts = useCallback(
        (contacts: ContactData[]): string => {
            const invalidContacts = contacts.filter(
                (contact) => !contact.isValid
            );
            if (invalidContacts.length > 0) {
                return t(
                    'components.member_next_of_kin_settings_section.error_incomplete_contacts'
                );
            }

            return '';
        },
        [t]
    );
    const contactsError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkContacts(contacts);
    }, [showRealTimeErrors, contacts, checkContacts]);

    function submitForm() {
        onSubmit?.({
            contacts: contacts.map((c) => {
                return {
                    name: c.name,
                    role: c.role,
                    phone: sanitisePhone(c.phone)
                };
            })
        });
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkContacts(contacts)) {
            setShowRealTimeErrors(false);
            submitForm();
        }
    };

    function resetForm() {
        setContacts(
            initialContacts.map((contact) => ({
                id: uuidv4(),
                isValid: false,
                ...contact
            }))
        );
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const sectionTitle = t(
        'components.member_next_of_kin_settings_section.title'
    );

    return (
        <UISection>
            {isEditing ? (
                <EditableForm
                    sectionTitle={sectionTitle}
                    contacts={contacts}
                    setContacts={setContacts}
                    contactsError={contactsError}
                    loading={loading}
                    showRealTimeErrors={showRealTimeErrors}
                    canEdit={canEdit}
                    resetForm={resetForm}
                    handleSubmit={handleSubmit}
                />
            ) : (
                <NonEditableForm
                    sectionTitle={sectionTitle}
                    contacts={contacts}
                    onEditClick={() => {
                        setIsEditing(true);
                        setShowRealTimeErrors(false);
                    }}
                    canEdit={canEdit}
                    editDisabledReason={editDisabledReason}
                />
            )}
        </UISection>
    );
});

function NonEditableForm(props: {
    sectionTitle: string;
    contacts: ContactData[];
    onEditClick: () => void;
    canEdit: boolean;
    editDisabledReason?: string;
}): ReactElement {
    const { sectionTitle, contacts, onEditClick, canEdit, editDisabledReason } =
        props;

    const { t } = useCompaniesTranslation();

    return (
        <>
            <UISectionHeader
                title={sectionTitle}
                rightButtons={[
                    {
                        text: t('shared.labels.edit'),
                        variant: 'textDefault',
                        type: 'button',
                        disabled: !canEdit,
                        disabledTooltip: editDisabledReason,
                        onClick: onEditClick
                    }
                ]}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <Description />

                    <div>
                        <ContactsList
                            contacts={contacts}
                            roleOrRelationship="relationship"
                        />
                    </div>
                </div>
            </UISectionContent>
        </>
    );
}

function EditableForm(props: {
    sectionTitle: string;
    contacts: ContactData[];
    setContacts: Dispatch<SetStateAction<ContactData[]>>;
    contactsError: string;
    loading: boolean;
    showRealTimeErrors: boolean;
    canEdit: boolean;
    resetForm: () => void;
    handleSubmit: FormEventHandler;
}): ReactElement {
    const {
        sectionTitle,
        contacts,
        setContacts,
        contactsError,
        loading,
        showRealTimeErrors,
        canEdit,
        resetForm,
        handleSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UISectionHeader title={sectionTitle} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <Description />

                    <div>
                        <EditableContactsList
                            contacts={contacts}
                            setContacts={setContacts}
                            showRealTimeErrors={showRealTimeErrors}
                            canEdit={canEdit}
                            roleOrRelationship="relationship"
                        />
                    </div>

                    {contactsError && (
                        <UIText type="error">{contactsError}</UIText>
                    )}
                </div>
            </UISectionContent>
            <UISectionFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: loading
                            ? t('shared.labels.saving')
                            : t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: resetForm
                    }
                ]}
            />
        </form>
    );
}

function Description(): ReactElement {
    const { t } = useCompaniesTranslation();

    return (
        <UIText type="default2">
            {t('components.member_next_of_kin_settings_section.intro')}
        </UIText>
    );
}
